import { useEffect, useRef, useCallback } from 'react';
import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';

export const useThreeScene = (canvasRef) => {
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const composerRef = useRef(null);
  const animationRef = useRef(null);
  const timerRef = useRef(null);
  const initedRef = useRef(false);

  const initScene = useCallback(() => {
    if (initedRef.current || !canvasRef.current) return false;
    initedRef.current = true;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000000);
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(
      50,
      window.innerWidth / window.innerHeight,
      0.1,
      100
    );
    const r = 7.0;
    const theta = 25 * Math.PI / 180;
    camera.position.set(r * Math.sin(theta), 0, r * Math.cos(theta));
    camera.lookAt(0, 0, 0);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({
      canvas: canvasRef.current,
      antialias: true,
      powerPreference: 'high-performance',
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;
    rendererRef.current = renderer;

    const composer = new EffectComposer(renderer);
    composer.addPass(new RenderPass(scene, camera));

    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(window.innerWidth, window.innerHeight),
      0.8,   
      0.3,   
      0.5    
    );
    composer.addPass(bloomPass);
    composer.addPass(new OutputPass());
    composerRef.current = composer;

    const timer = new THREE.Timer();
    timer.connect(document);
    timerRef.current = timer;

    return true;
  }, [canvasRef]);

  const render = useCallback(() => {
    if (composerRef.current) {
      composerRef.current.render();
    }
  }, []);

  const updateTimer = useCallback(() => {
    if (timerRef.current) timerRef.current.update();
  }, []);

  const getDelta = useCallback(() => {
    return timerRef.current ? timerRef.current.getDelta() : 0;
  }, []);

  const getElapsed = useCallback(() => {
    return timerRef.current ? timerRef.current.getElapsed() : 0;
  }, []);

  const handleResize = useCallback(() => {
    if (!cameraRef.current || !rendererRef.current || !composerRef.current) return;
    const w = window.innerWidth;
    const h = window.innerHeight;
    cameraRef.current.aspect = w / h;
    cameraRef.current.updateProjectionMatrix();
    rendererRef.current.setSize(w, h);
    composerRef.current.setSize(w, h);
  }, []);

  const dispose = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }

    if (timerRef.current) {
      timerRef.current.dispose();
      timerRef.current = null;
    }

    if (sceneRef.current) {
      sceneRef.current.traverse((object) => {
        if (object.geometry) object.geometry.dispose();
        if (object.material) {
          if (Array.isArray(object.material)) {
            object.material.forEach(m => m.dispose());
          } else {
            object.material.dispose();
          }
        }
      });
      sceneRef.current = null;
    }

    if (composerRef.current) {
      composerRef.current.dispose();
      composerRef.current = null;
    }

    if (rendererRef.current) {
      rendererRef.current.dispose();
      rendererRef.current = null;
    }

    initedRef.current = false;
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  return {
    sceneRef,
    cameraRef,
    rendererRef,
    composerRef,
    animationRef,
    initScene,
    render,
    updateTimer,
    getDelta,
    getElapsed,
    dispose,
  };
};
